import { ReactNode, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AdminAffiliateDetail, AdminAffiliateRow } from 'src/types/Affiliate'
import { dollarsToCents, formatBps, formatCents, percentToBps } from 'src/utils/affiliateFormat'
import styled from 'styled-components'
import { Muted } from '../affiliate/ui'

export const useDebounced = <T,>(value: T, delay = 350) => {
    const [debounced, setDebounced] = useState(value)
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delay)
        return () => clearTimeout(timer)
    }, [value, delay])
    return debounced
}

/** After an action empties the last page, step back to the last page that still has rows. */
export const useClampPage = (
    page: number,
    totalPages: number | undefined,
    setPage: (page: number) => void,
    isPlaceholder: boolean
) =>
    useEffect(() => {
        if (!isPlaceholder && totalPages !== undefined && totalPages > 0 && page > totalPages) setPage(totalPages)
    }, [page, totalPages, isPlaceholder, setPage])

export const CODE_PATTERN = /^[a-z0-9-]{3,20}$/

export const userLabel = (user: AdminAffiliateRow['user']) => {
    if (!user) return '—'
    if (user.email && user.discordUsername) return `${user.email} (@${user.discordUsername})`
    return user.email || (user.discordUsername && `@${user.discordUsername}`) || user.id
}

/** Thrown by the form readers below; the message is safe to show. */
export class FormError extends Error {}

/** Optional percent field → bps, or undefined when blank. */
export const readPercent = (value: string, label: string) => {
    if (!value.trim()) return undefined
    const bps = percentToBps(value)
    if (!Number.isFinite(bps) || bps < 0 || bps > 10000) throw new FormError(`${label} must be between 0 and 100%.`)
    return bps
}

/** Optional dollar field → cents, or undefined when blank. */
export const readDollars = (value: string, label: string) => {
    if (!value.trim()) return undefined
    const cents = dollarsToCents(value)
    if (!Number.isFinite(cents) || cents < 0) throw new FormError(`${label} must be a positive dollar amount.`)
    return cents
}

export const readInt = (value: string, label: string, min: number, max: number) => {
    const number = Number(value)
    if (!value.trim() || !Number.isInteger(number) || number < min || number > max) {
        throw new FormError(`${label} must be a whole number between ${min} and ${max}.`)
    }
    return number
}

export const formMessage = (err: unknown) => (err instanceof FormError ? err.message : null)

export const Mono = styled.span`
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.6);
    word-break: break-all;
`

export const ClickRow = styled.tr`
    cursor: pointer;

    &:hover td {
        background: rgba(255, 255, 255, 0.03);
    }
`

export const InlineLink = styled(Link)`
    color: #8fa0ff;
    font-weight: 600;
`

export const Negative = styled.span<{ $negative: boolean }>`
    color: ${({ $negative }) => ($negative ? '#ff8a94' : 'inherit')};
`

export const Actions = styled.div`
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: flex-end;
`

export const FieldGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
`

export const SmallButtonStyle = { minHeight: 34, padding: '0 12px', fontSize: 13 }

const isHttpUrl = (url: string) => /^https?:\/\//i.test(url)

export const SafeExternalLink = ({ url, children }: { url: string; children?: ReactNode }) =>
    isHttpUrl(url) ? (
        <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#8fa0ff', wordBreak: 'break-all' }}>
            {children || url}
        </a>
    ) : (
        <span style={{ wordBreak: 'break-all' }}>{url}</span>
    )

export const ApplicationDetails = ({ application }: { application: AdminAffiliateDetail['application'] }) => {
    if (!application) return <Muted>No application details.</Muted>
    const socials = application.socials || []
    return (
        <FieldGroup>
            {socials.length > 0 ? (
                <ul style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: 0, listStyle: 'none' }}>
                    {socials.map((social, index) => (
                        <li key={index} style={{ fontSize: 14 }}>
                            <Muted as="span">{social.platform}:</Muted> <SafeExternalLink url={social.url} />
                        </li>
                    ))}
                </ul>
            ) : (
                <Muted>No socials listed.</Muted>
            )}
            <p style={{ fontSize: 14 }}>
                <Muted as="span">Audience size:</Muted> {application.audienceSize || '—'}
            </p>
            {application.message ? (
                <p style={{ fontSize: 14, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{application.message}</p>
            ) : (
                <Muted>No message.</Muted>
            )}
        </FieldGroup>
    )
}

/** Program history and similar values: show primitives as-is, anything else as JSON. */
export const formatValue = (value: unknown, field = '') => {
    if (value === null || value === undefined) return '—'
    if (typeof value === 'number' && field.endsWith('Bps')) return formatBps(value)
    if (typeof value === 'number' && field.endsWith('Cents')) return formatCents(value)
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value)
    return JSON.stringify(value)
}
